import React from 'react';
import styled from 'styled-components';
import {
  Accordion,
  Button,
  Typography,
  Select,
  IconButton,
  FormControlLabel,
  DialogContent,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import Grid from '@mui/material/Grid';

const styles = theme => ({
  headerLeft: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    maxWidth: 570,
    gridColumnStart: 1,
    gridColumnEnd: 3,
  },
  headerRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  main: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gridColumnStart: 1,
    gridColumnEnd: 4,
  },
  wrapCode: {
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
    padding: 24,
    background: theme.palette.background.paper,
    marginBottom: 16,
  },
  label: {
    fontWeight: 600,
    alignItems: 'center',
    display: 'grid',
  },
  secondLevelLabel: {
    fontWeight: 400,
    alignItems: 'center',
    display: 'grid',
  },
  select: {
    width: '100%',
    display: 'inline-block',
  },
  wrapFlexCenter: {
    display: 'flex',
    justifyContent: 'center',
    margin: '32px 0',
  },
  textContentCenter: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content',
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
});

export const StyledAccordion = styled(Accordion)({
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    padding: '0',
  },
  '& .MuiAccordionDetails-root': {
    padding: '8px 0',
  },
});

export const AddCredential = styled('span')(({ color, hoverColor }) => ({
  fontSize: '14px',
  color: color ? color : 'rgba(33, 150, 243, 1)',
  '&:hover': {
    cursor: 'pointer',
    color: hoverColor ? hoverColor : 'rgba(33, 150, 243, 0.85)',
  },
}));

export const StyledInstructions = styled('div')({
  width: '100%',
  borderRadius: '8px',
  border: '1px solid #55A77A',
  backgroundColor: '#55A77A12',
  padding: '12px 16px',
  margin: '8px 0',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',

  '& ul': {
    margin: '0',
    padding: '0 0 0 20px',
  },

  '& li': {
    marginBottom: '10px',
    position: 'relative',
    paddingLeft: '4px',
    lineHeight: '1.5',

    '&:last-child': {
      marginBottom: '0',
    },
  },

  '& b': {
    backgroundColor: 'rgba(85, 167, 122, 0.15)',
    padding: '2px 5px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '0.95em',
  },
});

export const FreeUserWarning = styled('div')({
  gap: '8px',
  padding: '8px',
  borderRadius: '6px',
  border: '1px solid #FFB74D',
  backgroundColor: '#FFB74D26',
});

export const StyledSecretPlaceholder = styled('span')({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const AceEditorContainer = styled('div')({
  width: '100%',
  height: '700px',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid var(--color-divider)',
});

export const PlaceholderItemContainer = styled('div')({
  width: '100%',
  height: '64px',
});

export const SecretDescriptionText = styled('div')({
  color: 'var(--mui-palette-text-secondary)',
});

export const SecretButton = React.forwardRef(function SecretButton({ sx, ...props }, ref) {
  return <Button ref={ref} {...props} sx={{ maxWidth: 400, ...sx }} />;
});

export const SecretButtonContent = styled(Grid)({
  flex: 1,
  minWidth: 0,
});

export const KeyIconStyle = styled(KeyIcon)({
  marginRight: '4px',
  opacity: 0.7,
});

export const MoreSecretsText = styled(Typography)({
  marginLeft: '4px',
  opacity: 0.7,
});

export const EditorControlsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '4px 0',
  marginBottom: '8px',
  boxShadow: 'none',
});

export const EditorActionsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const BaseSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '6px 10px',
    borderRadius: '6px',
    fontWeight: 500,
    backgroundColor: 'var(--color-bg-subtle)',
    border: '1px solid var(--color-divider)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'var(--color-hover)',
      borderColor: 'var(--color-text-disabled)',
    },
  },
});

export const CodeLanguageSelect = styled(BaseSelect)({
  minWidth: '120px',
});

export const ThemeSelect = styled(BaseSelect)({
  minWidth: '110px',
});

export const EditorActionButton = styled(IconButton)({
  backgroundColor: 'var(--color-bg-subtle)',
  border: '1px solid var(--color-divider)',
  borderRadius: '6px',
  padding: '4px',
  transition: 'all 0.2s ease',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'var(--color-hover)',
    borderColor: 'var(--color-text-disabled)',
    boxShadow: 'none',
  },
});

export const EditorSwitchLabel = styled(FormControlLabel)({
  marginRight: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 500,
    marginLeft: '4px',
  },
});

export const FullscreenDialogContent = styled(DialogContent)({
  padding: '16px',
  backgroundColor: 'var(--color-bg-subtle)',
  boxShadow: 'none',
});

export const EditorContainer = styled('div')({
  height: '80vh',
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid var(--color-divider)',
  boxShadow: 'none',
});

export default styles;
